import { useEffect, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Box, CircularProgress, Typography } from '@mui/material'
import { useTranslation } from '../../../shared/hooks/useIntractionTranslate'
import { INTERAX_COLORS } from '../../../constants/colors'
import { ORG_SWITCH_OVERLAY_Z_INDEX } from './orgSwitchConstants'
import { useOrgSwitch } from './OrgSwitchContext'
import type { OrgSwitchPhase } from './OrgSwitchContext'

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false)

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    setReduced(mq.matches)
    const handler = () => setReduced(mq.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  return reduced
}

function getPhaseTitleKey(phase: OrgSwitchPhase): string {
  if (phase === 'auth') return 'org.switcher.switchingAuth'
  return 'org.switcher.switchingTitle'
}

export function OrgSwitchLoadingOverlay(): ReactNode {
  const { isSwitching, phase, targetLabel } = useOrgSwitch()
  const { t } = useTranslation()
  const prefersReducedMotion = usePrefersReducedMotion()
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
    return () => setMounted(false)
  }, [])

  useEffect(() => {
    if (!isSwitching) {
      document.body.style.overflow = ''
      return
    }
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [isSwitching])

  if (!isSwitching || !mounted) {
    return null
  }

  const titleKey = getPhaseTitleKey(phase)
  const description =
    targetLabel != null && targetLabel !== ''
      ? t('org.switcher.switchingDescription', { orgName: targetLabel })
      : undefined

  const overlayContent = (
    <Box
      role="status"
      aria-live="polite"
      aria-busy="true"
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        backdropFilter: prefersReducedMotion ? 'none' : 'blur(4px)',
        zIndex: ORG_SWITCH_OVERLAY_Z_INDEX,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        color: INTERAX_COLORS.white,
        pointerEvents: 'all'
      }}
    >
      <CircularProgress
        size={64}
        sx={{
          color: INTERAX_COLORS.sun,
          marginBottom: { xs: 2, sm: 3, md: 3 }
        }}
      />
      <Typography
        sx={{
          fontSize: { xs: '20px', sm: '22px', md: '24px' },
          fontWeight: 600,
          color: INTERAX_COLORS.white,
          marginBottom: description ? 1 : 0,
          textAlign: 'center',
          maxWidth: '600px',
          px: { xs: 2, sm: 3, md: 3 }
        }}
      >
        {t(titleKey)}
      </Typography>
      {description ? (
        <Typography
          sx={{
            fontSize: { xs: '13px', sm: '14px', md: '14px' },
            color: INTERAX_COLORS.thunderGrey,
            textAlign: 'center',
            maxWidth: '400px',
            px: { xs: 2, sm: 3, md: 3 }
          }}
        >
          {description}
        </Typography>
      ) : null}
    </Box>
  )

  return createPortal(overlayContent, document.body) as ReactNode
}
